import React, { useState, useEffect } from 'react';
import {
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Refresh,
  AccountBalanceWallet,
  ShowChart,
  People,
  AttachMoney,
  Timeline,
  ContentCopy,
  Person,
  VerifiedUser,
  OpenInNew,
  LocalFireDepartment,
  Inventory
} from '@mui/icons-material';
import {
  AreaChart, Area,
  XAxis, YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart, Pie, Cell,
  Legend
} from 'recharts';
import api from '../services/api';
import { formatNumber, formatCurrency } from '../utils/formatters';

// Цветовая палитра
const COLORS = {
  primary: '#8a2be2',
  secondary: '#9932cc',
  success: '#4caf50',
  warning: '#ff9800',
  error: '#f44336',
  info: '#2196f3'
};

// Яркие контрастные цвета для pie chart
const PIE_COLORS = [
  '#FF6B6B', // Красный - Киты 1M+
  '#FFD93D', // Жёлтый - 100K-1M
  '#6BCB77', // Зелёный - 10K-100K
  '#4D96FF', // Синий - 1K-10K
  '#C0C0C0'  // Серый - <1K
];

// Карточка метрики
const MetricCard = ({ title, value, change, icon, color, loading, suffix = '' }) => {
  const isPositive = change >= 0;

  return (
    <Card sx={{
      background: `linear-gradient(135deg, ${color}22 0%, ${color}11 100%)`,
      border: `1px solid ${color}44`,
      height: '100%'
    }}>
      <CardContent>
        {loading ? (
          <>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="80%" height={40} />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {title}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                  {value}{suffix}
                </Typography>
                {change !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isPositive ?
                      <TrendingUp sx={{ color: COLORS.success, mr: 0.5, fontSize: 18 }} /> :
                      <TrendingDown sx={{ color: COLORS.error, mr: 0.5, fontSize: 18 }} />
                    }
                    <Typography
                      variant="body2"
                      sx={{ color: isPositive ? COLORS.success : COLORS.error }}
                    >
                      {isPositive ? '+' : ''}{change.toFixed(2)}%
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                      24h
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{
                background: color,
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {React.cloneElement(icon, { sx: { color: 'white', fontSize: 28 } })}
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Таймфреймы для графика
const TIMEFRAMES = [
  { value: '1h', label: '1 час' },
  { value: '1d', label: '1 день' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' }
];

function TokenAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [timeframe, setTimeframe] = useState('7d');

  useEffect(() => {
    loadData(timeframe);
    const interval = setInterval(() => loadData(timeframe), 60000);
    return () => clearInterval(interval);
  }, [timeframe]);

  const loadData = async (tf = '7d') => {
    try {
      setRefreshing(true);
      const response = await api.get(`/admin/token-analytics?timeframe=${tf}`);
      if (response.data?.success) {
        setData(response.data.data);
        setError(null);
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Ошибка загрузки аналитики токена:', err);
      setError('Не удалось загрузить данные');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleTimeframeChange = (event, newTimeframe) => {
    if (newTimeframe !== null) {
      setTimeframe(newTimeframe);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.post('/admin/token-analytics/refresh');
      if (response.data?.success) {
        setData(response.data.data);
        setError(null);
      }
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Ошибка обновления:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Подготовка данных для PieChart распределения холдеров
  const getDistributionData = () => {
    if (!data?.holders?.distribution) return [];
    const dist = data.holders.distribution;
    return [
      { name: '1M+ (Киты)', value: dist['whale_1m_plus'] || 0 },
      { name: '100K-1M', value: dist['large_100k_1m'] || 0 },
      { name: '10K-100K', value: dist['medium_10k_100k'] || 0 },
      { name: '1K-10K', value: dist['small_1k_10k'] || 0 },
      { name: '<1K', value: dist['micro_under_1k'] || 0 }
    ].filter(item => item.value > 0);
  };

  const dex = data?.dexscreener || {};
  const holders = data?.holders || {};

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Token Analytics
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Аналитика токена SPERM | Последнее обновление: {lastUpdate.toLocaleTimeString('ru-RU')}
          </Typography>
        </Box>
        <Tooltip title="Обновить данные">
          <IconButton onClick={handleRefresh} disabled={refreshing}>
            <Refresh className={refreshing ? 'rotating' : ''} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Контракты */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Контракты</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">Jetton:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {data?.contracts?.jetton || '...'}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(data?.contracts?.jetton)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="textSecondary">Pair:</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {data?.contracts?.pair || '...'}
                </Typography>
                <IconButton size="small" onClick={() => copyToClipboard(data?.contracts?.pair)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Supply Breakdown - Разбивка supply */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f4433622 0%, #ff980011 100%)', border: '1px solid #f4433644' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocalFireDepartment sx={{ color: COLORS.error }} />
            <Typography variant="h6">Supply & Burn</Typography>
          </Box>

          <Grid container spacing={3}>
            {/* Изначальный supply */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">Изначальный Supply</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {formatNumber(data?.supply_breakdown?.initial_supply || 2000000)}
                </Typography>
                <Typography variant="caption" color="textSecondary">SPERM</Typography>
              </Box>
            </Grid>

            {/* Текущий total supply */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="caption" color="textSecondary">Текущий Total Supply</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: COLORS.primary }}>
                  {formatNumber(data?.supply_breakdown?.total_supply || 0)}
                </Typography>
                <Typography variant="caption" color="textSecondary">SPERM (на блокчейне)</Typography>
              </Box>
            </Grid>

            {/* Сожжено */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: `${COLORS.error}11`, borderRadius: 2, textAlign: 'center', border: `1px solid ${COLORS.error}44` }}>
                <Typography variant="caption" color="textSecondary">
                  <LocalFireDepartment sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                  Сожжено
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: COLORS.error }}>
                  {formatNumber(data?.supply_breakdown?.total_burned || 0)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  SPERM ({data?.supply_breakdown?.burn_count || 0} сжиганий)
                </Typography>
              </Box>
            </Grid>

            {/* Циркуляция */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: `${COLORS.success}11`, borderRadius: 2, textAlign: 'center', border: `1px solid ${COLORS.success}44` }}>
                <Typography variant="caption" color="textSecondary">Циркуляция в рынке</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: COLORS.success }}>
                  {formatNumber(data?.supply_breakdown?.circulation || 0)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  SPERM ({data?.supply_breakdown?.circulation_percentage || 0}%)
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Кошельки проекта */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AccountBalanceWallet fontSize="small" />
              Кошельки проекта (исключены из циркуляции)
            </Typography>
            <Grid container spacing={2}>
              {/* Главный кошелек */}
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: `${COLORS.warning}11`, borderRadius: 2, border: `1px solid ${COLORS.warning}33` }}>
                  <Typography variant="caption" color="textSecondary">🏦 Главный кошелек</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.warning, my: 0.5 }}>
                    {formatNumber(data?.supply_breakdown?.wallets?.main?.balance || 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'text.secondary' }}>
                      {(data?.supply_breakdown?.wallets?.main?.address || '').substring(0, 20)}...
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(data?.supply_breakdown?.wallets?.main?.address)}>
                      <ContentCopy sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => window.open(`https://tonviewer.com/${data?.supply_breakdown?.wallets?.main?.address}`, '_blank')}>
                      <OpenInNew sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
              {/* Маркетмейкер */}
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: `${COLORS.info}11`, borderRadius: 2, border: `1px solid ${COLORS.info}33` }}>
                  <Typography variant="caption" color="textSecondary">📊 Маркетмейкер</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.info, my: 0.5 }}>
                    {formatNumber(data?.supply_breakdown?.wallets?.market_maker?.balance || 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'text.secondary' }}>
                      {(data?.supply_breakdown?.wallets?.market_maker?.address || '').substring(0, 20)}...
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(data?.supply_breakdown?.wallets?.market_maker?.address)}>
                      <ContentCopy sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => window.open(`https://tonviewer.com/${data?.supply_breakdown?.wallets?.market_maker?.address}`, '_blank')}>
                      <OpenInNew sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
              {/* Кошелек выводов */}
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, bgcolor: `${COLORS.primary}11`, borderRadius: 2, border: `1px solid ${COLORS.primary}33` }}>
                  <Typography variant="caption" color="textSecondary">💸 Кошелек выводов</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.primary, my: 0.5 }}>
                    {formatNumber(data?.supply_breakdown?.wallets?.withdrawals?.balance || 0)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'text.secondary' }}>
                      {(data?.supply_breakdown?.wallets?.withdrawals?.address || '').substring(0, 20)}...
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(data?.supply_breakdown?.wallets?.withdrawals?.address)}>
                      <ContentCopy sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => window.open(`https://tonviewer.com/${data?.supply_breakdown?.wallets?.withdrawals?.address}`, '_blank')}>
                      <OpenInNew sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            {/* Итого на кошельках */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                Итого на кошельках проекта: <strong>{formatNumber(data?.supply_breakdown?.wallets?.total || 0)} SPERM</strong>
              </Typography>
            </Box>
          </Box>

          {/* Дата последнего сжигания */}
          {data?.supply_breakdown?.last_burn_date && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                Последнее сжигание: {new Date(data.supply_breakdown.last_burn_date).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Основные метрики */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Цена USD"
            value={`$${formatNumber(dex.price_usd || 0, 4)}`}
            change={dex.price_change_24h}
            icon={<AttachMoney />}
            color={COLORS.success}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Market Cap"
            value={`$${formatNumber(dex.market_cap || 0)}`}
            icon={<ShowChart />}
            color={COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Ликвидность"
            value={`$${formatNumber(dex.liquidity_usd || 0)}`}
            icon={<AccountBalanceWallet />}
            color={COLORS.info}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${COLORS.warning}22 0%, ${COLORS.warning}11 100%)`,
            border: `1px solid ${COLORS.warning}44`,
            height: '100%'
          }}>
            <CardContent>
              {loading ? (
                <>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="80%" height={40} />
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        Холдеров
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>
                        {formatNumber(holders.total || 0)}
                      </Typography>
                      {holders.change_24h !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {holders.change_24h >= 0 ?
                            <TrendingUp sx={{ color: COLORS.success, mr: 0.5, fontSize: 18 }} /> :
                            <TrendingDown sx={{ color: COLORS.error, mr: 0.5, fontSize: 18 }} />
                          }
                          <Typography
                            variant="body2"
                            sx={{ color: holders.change_24h >= 0 ? COLORS.success : COLORS.error, fontWeight: 'bold' }}
                          >
                            {holders.change_24h >= 0 ? '+' : ''}{holders.change_24h}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                            за 24ч
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{
                      background: COLORS.warning,
                      borderRadius: 2,
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <People sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Объёмы торгов */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Объёмы торгов
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">5 мин</Typography>
                    <Typography variant="h6">${formatNumber(dex.volume_5m || 0)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">1 час</Typography>
                    <Typography variant="h6">${formatNumber(dex.volume_1h || 0)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">6 часов</Typography>
                    <Typography variant="h6">${formatNumber(dex.volume_6h || 0)}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="caption" color="textSecondary">24 часа</Typography>
                    <Typography variant="h6" sx={{ color: COLORS.success }}>${formatNumber(dex.volume_24h || 0)}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Изменение цены</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Chip
                      label={`5м: ${dex.price_change_5m >= 0 ? '+' : ''}${(dex.price_change_5m || 0).toFixed(2)}%`}
                      color={dex.price_change_5m >= 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={`1ч: ${dex.price_change_1h >= 0 ? '+' : ''}${(dex.price_change_1h || 0).toFixed(2)}%`}
                      color={dex.price_change_1h >= 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={`6ч: ${dex.price_change_6h >= 0 ? '+' : ''}${(dex.price_change_6h || 0).toFixed(2)}%`}
                      color={dex.price_change_6h >= 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Chip
                      label={`24ч: ${dex.price_change_24h >= 0 ? '+' : ''}${(dex.price_change_24h || 0).toFixed(2)}%`}
                      color={dex.price_change_24h >= 0 ? 'success' : 'error'}
                      size="small"
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Транзакции 24ч</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip
                    icon={<TrendingUp />}
                    label={`Покупки: ${dex.txns_buys_24h || 0}`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip
                    icon={<TrendingDown />}
                    label={`Продажи: ${dex.txns_sells_24h || 0}`}
                    color="error"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Распределение холдеров */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Распределение холдеров
              </Typography>
              {loading ? (
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
              ) : getDistributionData().length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={getDistributionData()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {getDistributionData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        background: 'rgba(30,41,59,0.95)',
                        border: `1px solid ${COLORS.primary}`,
                        borderRadius: 8,
                        color: '#fff'
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="textSecondary">Нет данных о холдерах</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* График цены */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
              История цены
            </Typography>
            <ToggleButtonGroup
              value={timeframe}
              exclusive
              onChange={handleTimeframeChange}
              size="small"
            >
              {TIMEFRAMES.map((tf) => (
                <ToggleButton
                  key={tf.value}
                  value={tf.value}
                  sx={{
                    px: 2,
                    '&.Mui-selected': {
                      bgcolor: COLORS.primary,
                      color: 'white',
                      '&:hover': {
                        bgcolor: COLORS.secondary
                      }
                    }
                  }}
                >
                  {tf.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          {loading ? (
            <Skeleton variant="rectangular" height={300} />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data?.price_history || []}>
                <defs>
                  <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis
                  dataKey="date"
                  stroke="rgba(255,255,255,0.5)"
                  style={{ fontSize: 10 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    if (timeframe === '1h' || timeframe === '1d') {
                      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
                    }
                    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
                  }}
                />
                <YAxis
                  stroke="rgba(255,255,255,0.5)"
                  style={{ fontSize: 12 }}
                  domain={['auto', 'auto']}
                  tickFormatter={(value) => `$${value.toFixed(2)}`}
                />
                <RechartsTooltip
                  contentStyle={{
                    background: 'rgba(30,41,59,0.95)',
                    border: `1px solid ${COLORS.primary}`,
                    borderRadius: 8,
                    color: '#fff'
                  }}
                  formatter={(value) => [`$${value.toFixed(4)}`, 'Цена']}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleString('ru-RU');
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="price"
                  stroke={COLORS.primary}
                  fillOpacity={1}
                  fill="url(#colorPrice)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Топ холдеры */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              <People sx={{ mr: 1, verticalAlign: 'middle' }} />
              Топ-30 холдеров
            </Typography>
            {holders.total_supply && (
              <Typography variant="body2" color="textSecondary">
                Total Supply: {formatNumber(holders.total_supply)} SPERM
              </Typography>
            )}
          </Box>
          {loading ? (
            <Skeleton variant="rectangular" height={400} />
          ) : holders.top_holders && holders.top_holders.length > 0 ? (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'background.paper', width: 40 }}>#</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Адрес</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }}>Пользователь</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }} align="right">Баланс</TableCell>
                    <TableCell sx={{ bgcolor: 'background.paper' }} align="right">Доля</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {holders.top_holders.map((holder, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: holder.system_wallet
                          ? `${holder.system_wallet.color}15`
                          : holder.user
                            ? `${COLORS.success}11`
                            : index < 3
                              ? `${COLORS.warning}11`
                              : 'inherit',
                        '&:hover': { bgcolor: `${COLORS.primary}11` }
                      }}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: index < 3 ? 'bold' : 'normal',
                            color: index < 3 ? COLORS.warning : 'inherit'
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Скопировать адрес" placement="top">
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                '&:hover': { color: COLORS.primary }
                              }}
                              onClick={() => copyToClipboard(holder.address)}
                            >
                              {holder.address || 'unknown'}
                            </Typography>
                          </Tooltip>
                          <Tooltip title="Скопировать">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(holder.address)}
                              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                            >
                              <ContentCopy sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Открыть в TonScan">
                            <IconButton
                              size="small"
                              onClick={() => window.open(`https://tonscan.org/address/${holder.address}`, '_blank')}
                              sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                            >
                              <OpenInNew sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {holder.system_wallet ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                              label={`${holder.system_wallet.emoji} ${holder.system_wallet.label}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                                bgcolor: `${holder.system_wallet.color}22`,
                                color: holder.system_wallet.color,
                                border: `1px solid ${holder.system_wallet.color}44`
                              }}
                            />
                          </Box>
                        ) : holder.user ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person sx={{ fontSize: 16, color: COLORS.success }} />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 500,
                                  color: COLORS.success
                                }}
                              >
                                {holder.user.display_name}
                              </Typography>
                              {holder.user.vip_status && (
                                <Chip
                                  label={holder.user.vip_status}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: `${COLORS.warning}22`,
                                    color: COLORS.warning
                                  }}
                                />
                              )}
                            </Box>
                            {holder.user.staking && holder.user.staking.total_staked > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: COLORS.primary,
                                  fontSize: '0.7rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                📦 Стейкинг: {formatNumber(holder.user.staking.total_staked)} SPERM
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                          <Typography variant="body2" sx={{ fontWeight: index < 3 ? 'bold' : 'normal' }}>
                            {formatNumber(holder.balance || 0)}
                          </Typography>
                          {holder.user && holder.user.staking && holder.user.staking.total_staked > 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                              на кошельке
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(parseFloat(holder.percentage || 0), 100)}
                            sx={{
                              flex: 1,
                              height: 6,
                              borderRadius: 3,
                              bgcolor: `${COLORS.primary}22`,
                              '& .MuiLinearProgress-bar': {
                                bgcolor: holder.system_wallet
                                  ? holder.system_wallet.color
                                  : holder.user
                                    ? COLORS.success
                                    : index < 3
                                      ? COLORS.warning
                                      : COLORS.primary,
                                borderRadius: 3
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 45, textAlign: 'right' }}>
                            {holder.percentage}%
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">Данные о холдерах загружаются...</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* CSS для анимации */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Box>
  );
}

export default TokenAnalytics;
